/**
 * Return-visits shared protocol (T004-BE, docs/IMPLEMENTATION_PLAN_RETURN_VISITS.md).
 *
 * Pure data + pure functions, NO imports: browser recorder (T008), counter
 * ingress (T006), aggregate reader (T007) and the local provider (T010) all
 * execute this exact code so their DTOs, validation and calendar math can
 * never diverge. No browser/network/auth/DB code lives here; every clock is
 * an explicit argument (`instant: Date` or `today: 'YYYY-MM-DD'`), nothing
 * reads the wall clock.
 *
 * Vocabulary:
 *   epoch      global launch date, identical for every client, never future
 *   day        a Europe/Rome calendar date 'YYYY-MM-DD' (the NEW-stream calendar)
 *   cohortWeek Monday of the week of FIRST observation (a real Monday)
 *   nextWeek   return in the calendar week after first observation
 *              (server window day in [W+7, W+13])
 *   within30   return on a later calendar day within 30 days of first
 *              (local exact check age<=30; server coarse window [W+1, W+36])
 *   completion-exclusive M: a cohort's measure for a metric is complete only
 *              once its whole observation window has elapsed: M=W+14 for
 *              nextWeek (the week after the return week starts), M=W+37 for
 *              within30 (day W+36 has ended). A report period [S,E) selects
 *              cohorts by S<M<=E (window FINISHED in the period) — never by
 *              the week the cohort STARTED, which selected zero monthly
 *              cohorts for last-30d in the discarded draft.
 *
 * Decoder contract: every decode* takes `unknown` (network/storage JSON) and
 * returns the typed value or `null` — never throws on untrusted data, never
 * echoes the offending value back (R4: no request data in logs). Calendar
 * helpers (addDays/mondayOfWeek/…) throw on invalid input: they are only ever
 * called with already-validated data, and a throw there is a programmer bug
 * (broken clock injection), not a protocol condition.
 */

// ---------------------------------------------------------------------------
// Constants (single source of truth; api/, web/ and the provider import these)
// ---------------------------------------------------------------------------

export const RETURN_VISITS_PROTOCOL_VERSION = 1;
export const RETURN_VISITS_TIMEZONE = 'Europe/Rome';

/** localStorage key holding the ReturnObservation record. */
export const RETURN_VISITS_OBSERVATION_KEY = 'buzzmap_return_visits';
/** localStorage key holding the opt-out marker. */
export const RETURN_VISITS_OPTOUT_KEY = 'buzzmap_return_visits_optout';
/** Origin-wide exclusive Web Lock name — never day/epoch-specific. */
export const RETURN_VISITS_LOCK_NAME = 'buzzmap-return-visits';

/** Logical observation lifetime: expires AT first+365 (day first+364 still lives). */
export const RETURN_VISITS_EXPIRY_DAYS = 365;
/** Audience aggregate retention (product limit, not a statutory safe harbor). */
export const RETURN_VISITS_RETENTION_DAYS = 400;
/** POST /api/return-visits body cap (1 KiB). */
export const RETURN_VISITS_MAX_PAYLOAD_BYTES = 1024;
/** Cohort queries load up to this many days BEFORE S (bounded by retention). */
export const RETURN_VISITS_COHORT_LOOKBACK_DAYS = 37;

/** completion-exclusive M = cohortWeek + offset, in days. */
export const RETURN_VISITS_NEXT_WEEK_COMPLETION_OFFSET_DAYS = 14;
export const RETURN_VISITS_WITHIN_30_COMPLETION_OFFSET_DAYS = 37;

/** Period vocabulary of the private report (v2 manifest options). */
export const RETURN_VISIT_PERIODS = ['last-7d', 'last-30d', 'last-90d', 'since-go-live'] as const;
export type ReturnVisitPeriod = (typeof RETURN_VISIT_PERIODS)[number];
export const RETURN_VISIT_PERIOD_DEFAULT: ReturnVisitPeriod = 'last-30d';

// ---------------------------------------------------------------------------
// DTOs (the explicit integration boundary — plan "Shared interfaces (T004)")
// ---------------------------------------------------------------------------

/** GET /api/return-visits?mode=config response. */
export interface ReturnVisitsConfig {
  version: 1;
  enabled: boolean;
  /** Global launch date; null only when disabled. Never future vs `today`. */
  epoch: string | null;
  /** Server's current Europe/Rome day at config generation. */
  today: string;
}

/**
 * POST /api/return-visits body, the ONLY thing a browser ever sends.
 * `new` forbids cohortWeek/flags entirely; `return` carries cohortWeek iff at
 * least one flag is true; flags are literal `true` only (never false, never an
 * opaque id). cohortWeek = Monday of the first-observation week.
 */
export interface ReturnContribution {
  version: 1;
  epoch: string;
  day: string;
  kind: 'new' | 'return';
  cohortWeek?: string;
  nextWeek?: true;
  within30?: true;
}

/** The localStorage observation record. first<=last, first>=epoch. */
export interface ReturnObservation {
  version: 1;
  epoch: string;
  first: string;
  last: string;
  nextWeek: boolean; // cohort flag already sent (sticky)
  within30: boolean; // cohort flag already sent (sticky)
  cohortAccepted: boolean; // NEW response confirmed while record still current
}

/** Result of the pure local transition: the contribution to POST (or none) and the record to store. */
export interface ReturnVisitTransition {
  contribution: ReturnContribution | null;
  observation: ReturnObservation | null;
}

/** One Rome-calendar day of browser activity in the report. */
export interface ReturnDay {
  day: string;
  newObservations: number;
  returningObservations: number;
  /** Day return share; null when unavailable. Definition owned by T007. */
  ratePct: number | null;
}

/** One weekly/monthly cohort row, plotted at completedOn. */
export interface ReturnCohort {
  cohortWeek: string; // Monday of the first-observation week
  completedOn: string; // M-1, the last day of the observation window (inside [S,E))
  newObservations: number;
  returnedObservations: number;
  /** weighted or per-cohort rate; null when denominator is zero or integrity invalid. */
  ratePct: number | null;
  integrity: 'ok' | 'invalid'; // invalid when returned>new — never clamp
}

/** The private report DTO (T007 output, T010 input). One asOfUtc for every bound. */
export interface ReturnVisitsReport {
  version: 1;
  status: 'ready' | 'empty' | 'unconfigured';
  epoch: string | null;
  collectionEnabled: boolean;
  /** Single normalized ISO UTC instant chosen at server request start. */
  asOfUtc: string;
  timezone: 'Europe/Rome';
  range: ReturnVisitRange;
  availableFrom: string | null;
  weeklyCoverageFrom: string | null;
  monthlyCoverageFrom: string | null;
  daily: ReturnDay[];
  weekly: ReturnCohort[];
  monthly: ReturnCohort[];
  /** Server-owned headline rates; provider displays, never recomputes. */
  weeklyRatePct: number | null;
  monthlyRatePct: number | null;
}

export interface ReturnVisitRange {
  period: ReturnVisitPeriod;
  from: string;
  toExclusive: string;
}

/** Bare [from,toExclusive) window used by cohort eligibility. */
export interface CohortRange {
  from: string;
  toExclusive: string;
}

export type ReturnCohortMetric = 'nextWeek' | 'within30';

/** Minimal shape the weighted-rate helper needs (subset of ReturnCohort). */
export type ReturnRateInput = Pick<ReturnCohort, 'newObservations' | 'returnedObservations' | 'integrity'>;

// ---------------------------------------------------------------------------
// Calendar helpers (Europe/Rome day via Intl; arithmetic on the UTC calendar)
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;
const CALENDAR_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True when `value` is a strict, real 'YYYY-MM-DD' calendar date (rejects
 * impossible dates such as 2026-02-30 or a non-leap 2027-02-29).
 */
export function isCalendarDay(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = CALENDAR_DAY_RE.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const ms = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(ms);
  return (
    roundTrip.getUTCFullYear() === year &&
    roundTrip.getUTCMonth() === month - 1 &&
    roundTrip.getUTCDate() === day
  );
}

function requireCalendarDay(day: string): number {
  if (!isCalendarDay(day)) throw new Error(`invalid calendar day: ${day}`);
  return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

/**
 * Calendar day (YYYY-MM-DD) of `instant` in Europe/Rome. Pure: the instant is
 * passed in; this never reads the clock. DST included (same Intl pattern as
 * api/lib/notifications.ts, re-implemented here so shared imports nothing).
 */
export function romeDay(instant: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: RETURN_VISITS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const datePart = (type: string) => parts.find((part) => part.type === type)!.value;
  return `${datePart('year')}-${datePart('month')}-${datePart('day')}`;
}

/** Pure calendar arithmetic on 'YYYY-MM-DD' (DST-proof: whole days only). */
export function addDays(day: string, days: number): string {
  if (!Number.isInteger(days)) throw new Error(`invalid day offset: ${days}`);
  const d = new Date(requireCalendarDay(day) + days * DAY_MS);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `earlier` to `later` (positive when later > earlier). */
export function diffDays(later: string, earlier: string): number {
  return Math.round((requireCalendarDay(later) - requireCalendarDay(earlier)) / DAY_MS);
}

/** True when `day` is a real calendar day that falls on a Monday. */
export function isMonday(day: string): boolean {
  return isCalendarDay(day) && new Date(requireCalendarDay(day)).getUTCDay() === 1;
}

/** Monday of the ISO week containing `day` (the cohort-week identity). */
export function mondayOfWeek(day: string): string {
  const dow = new Date(requireCalendarDay(day)).getUTCDay(); // 0=Sun .. 6=Sat
  const backToMonday = (dow + 6) % 7;
  return addDays(day, -backToMonday);
}

// ---------------------------------------------------------------------------
// Strict runtime decoders (unknown -> typed | null; exact keys, no leaks)
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => key in value);
}

/** No unknown keys: every present key must be in `allowed` (required ones checked separately). */
function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function isNonNegativeSafeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isRatePct(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100);
}

const CONFIG_KEYS = ['version', 'enabled', 'epoch', 'today'] as const;

/** Strict decoder for the config GET response. enabled=true REQUIRES a non-null, non-future epoch. */
export function decodeReturnVisitsConfig(value: unknown): ReturnVisitsConfig | null {
  if (!isPlainObject(value) || !hasExactKeys(value, CONFIG_KEYS)) return null;
  if (value.version !== RETURN_VISITS_PROTOCOL_VERSION) return null;
  if (typeof value.enabled !== 'boolean') return null;
  if (!isCalendarDay(value.today)) return null;
  if (value.epoch !== null && !isCalendarDay(value.epoch)) return null;
  // epoch is never future, whatever the enabled state
  if (value.epoch !== null && value.epoch > value.today) return null;
  if (value.enabled && value.epoch === null) return null;
  return { version: 1, enabled: value.enabled, epoch: value.epoch, today: value.today };
}

const NEW_KEYS = ['version', 'epoch', 'day', 'kind'] as const;
const RETURN_KEYS = ['version', 'epoch', 'day', 'kind', 'cohortWeek', 'nextWeek', 'within30'] as const;
const RETURN_REQUIRED_KEYS = ['version', 'epoch', 'day', 'kind'] as const;

/** `new` contribution: exact key set (forbids cohortWeek/flags entirely). */
function decodeNewContribution(value: Record<string, unknown>, epoch: string, day: string): ReturnContribution | null {
  if (!hasExactKeys(value, NEW_KEYS)) return null;
  return { version: 1, epoch, day, kind: 'new' };
}

/** Validates the flag/cohortWeek combination of a `return` (the tight part). */
function returnFlagsValid(value: Record<string, unknown>, epoch: string, day: string): boolean {
  const nextWeek = 'nextWeek' in value;
  const within30 = 'within30' in value;
  const hasCohortWeek = 'cohortWeek' in value;
  if (nextWeek && value.nextWeek !== true) return false; // literal true only, never false
  if (within30 && value.within30 !== true) return false;
  const flagCount = Number(nextWeek) + Number(within30);
  // cohortWeek present iff at least one flag is true
  if (hasCohortWeek !== (flagCount > 0)) return false;
  if (!hasCohortWeek) return true;
  const cohortWeek = value.cohortWeek;
  if (!isCalendarDay(cohortWeek) || !isMonday(cohortWeek)) return false;
  // ingress threshold is Monday(epoch): launch-week data may be counted;
  // REPORT eligibility is stricter (W>=epoch, excludes partial week)
  if (cohortWeek < mondayOfWeek(epoch)) return false;
  if (nextWeek && mondayOfWeek(day) !== addDays(cohortWeek, 7)) return false;
  if (within30) {
    const age = diffDays(day, cohortWeek);
    if (age < 1 || age > 36) return false;
  }
  return true;
}

/** `return` contribution: required keys + no unknown keys + valid flag/cohort combo. */
function decodeReturnContributionKind(value: Record<string, unknown>, epoch: string, day: string): ReturnContribution | null {
  if (!hasOnlyKeys(value, RETURN_KEYS)) return null;
  for (const key of RETURN_REQUIRED_KEYS) {
    if (!(key in value)) return null;
  }
  if (!returnFlagsValid(value, epoch, day)) return null;
  if ('cohortWeek' in value) {
    const contribution: ReturnContribution = { version: 1, epoch, day, kind: 'return', cohortWeek: value.cohortWeek as string };
    if ('nextWeek' in value) contribution.nextWeek = true;
    if ('within30' in value) contribution.within30 = true;
    return contribution;
  }
  return { version: 1, epoch, day, kind: 'return' };
}

/** Strict decoder for the POST body. See ReturnContribution for the flag rules. */
export function decodeReturnContribution(value: unknown): ReturnContribution | null {
  if (!isPlainObject(value)) return null;
  if (value.version !== RETURN_VISITS_PROTOCOL_VERSION) return null;
  if (value.kind !== 'new' && value.kind !== 'return') return null;
  if (!isCalendarDay(value.epoch) || !isCalendarDay(value.day)) return null;
  if (value.day < value.epoch) return null;
  if (value.kind === 'new') return decodeNewContribution(value, value.epoch, value.day);
  return decodeReturnContributionKind(value, value.epoch, value.day);
}

const OBSERVATION_KEYS = ['version', 'epoch', 'first', 'last', 'nextWeek', 'within30', 'cohortAccepted'] as const;

/** Strict decoder for the localStorage record. */
export function decodeReturnObservation(value: unknown): ReturnObservation | null {
  if (!isPlainObject(value) || !hasExactKeys(value, OBSERVATION_KEYS)) return null;
  if (value.version !== RETURN_VISITS_PROTOCOL_VERSION) return null;
  if (!isCalendarDay(value.epoch) || !isCalendarDay(value.first) || !isCalendarDay(value.last)) return null;
  if (value.first < value.epoch || value.first > value.last) return null;
  if (typeof value.nextWeek !== 'boolean' || typeof value.within30 !== 'boolean' || typeof value.cohortAccepted !== 'boolean') {
    return null;
  }
  return {
    version: 1,
    epoch: value.epoch,
    first: value.first,
    last: value.last,
    nextWeek: value.nextWeek,
    within30: value.within30,
    cohortAccepted: value.cohortAccepted,
  };
}

const DAY_KEYS = ['day', 'newObservations', 'returningObservations', 'ratePct'] as const;

/** Strict decoder for one report day row. Counts are nonnegative safe ints. */
export function decodeReturnDay(value: unknown): ReturnDay | null {
  if (!isPlainObject(value) || !hasExactKeys(value, DAY_KEYS)) return null;
  if (!isCalendarDay(value.day)) return null;
  if (!isNonNegativeSafeInt(value.newObservations) || !isNonNegativeSafeInt(value.returningObservations)) return null;
  if (!isRatePct(value.ratePct)) return null;
  return {
    day: value.day,
    newObservations: value.newObservations,
    returningObservations: value.returningObservations,
    ratePct: value.ratePct,
  };
}

const COHORT_KEYS = ['cohortWeek', 'completedOn', 'newObservations', 'returnedObservations', 'ratePct', 'integrity'] as const;

/**
 * Strict decoder for one report cohort row. Integrity rules are part of the
 * contract: integrity 'invalid' requires a null rate (returned>new is never
 * clamped); integrity 'ok' requires returned<=new; a zero denominator
 * (newObservations===0) requires a null rate.
 */
export function decodeReturnCohort(value: unknown): ReturnCohort | null {
  if (!isPlainObject(value) || !hasExactKeys(value, COHORT_KEYS)) return null;
  if (!isCalendarDay(value.cohortWeek) || !isCalendarDay(value.completedOn)) return null;
  if (!isNonNegativeSafeInt(value.newObservations) || !isNonNegativeSafeInt(value.returnedObservations)) return null;
  if (!isRatePct(value.ratePct)) return null;
  if (value.integrity !== 'ok' && value.integrity !== 'invalid') return null;
  if (value.integrity === 'invalid') {
    if (value.ratePct !== null) return null;
  } else {
    if (value.returnedObservations > value.newObservations) return null; // must be marked invalid
    if (value.newObservations === 0 && value.ratePct !== null) return null; // 0/0 is undefined, not 0%
  }
  return {
    cohortWeek: value.cohortWeek,
    completedOn: value.completedOn,
    newObservations: value.newObservations,
    returnedObservations: value.returnedObservations,
    ratePct: value.ratePct,
    integrity: value.integrity,
  };
}

const REPORT_KEYS = [
  'version',
  'status',
  'epoch',
  'collectionEnabled',
  'asOfUtc',
  'timezone',
  'range',
  'availableFrom',
  'weeklyCoverageFrom',
  'monthlyCoverageFrom',
  'daily',
  'weekly',
  'monthly',
  'weeklyRatePct',
  'monthlyRatePct',
] as const;
const RANGE_KEYS = ['period', 'from', 'toExclusive'] as const;
const AS_OF_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export function isReturnVisitPeriod(value: unknown): value is ReturnVisitPeriod {
  return typeof value === 'string' && (RETURN_VISIT_PERIODS as readonly string[]).includes(value);
}

type ReportStatus = ReturnVisitsReport['status'];

function decodeReportStatus(value: unknown): ReportStatus | null {
  if (value === 'ready' || value === 'empty' || value === 'unconfigured') return value;
  return null;
}

function decodeReportAsOfUtc(value: unknown): string | null {
  if (typeof value !== 'string' || !AS_OF_UTC_RE.test(value)) return null;
  if (!isCalendarDay(value.slice(0, 10)) || !Number.isFinite(Date.parse(value))) return null;
  return value;
}

interface ReportScalars {
  status: ReportStatus;
  epoch: string | null;
  collectionEnabled: boolean;
  asOfUtc: string;
}

function decodeReportScalars(value: Record<string, unknown>): ReportScalars | null {
  if (value.version !== RETURN_VISITS_PROTOCOL_VERSION) return null;
  const status = decodeReportStatus(value.status);
  if (status === null) return null;
  if (value.epoch !== null && !isCalendarDay(value.epoch)) return null;
  if (typeof value.collectionEnabled !== 'boolean') return null;
  const asOfUtc = decodeReportAsOfUtc(value.asOfUtc);
  if (asOfUtc === null) return null;
  return { status, epoch: value.epoch, collectionEnabled: value.collectionEnabled, asOfUtc };
}

function decodeReportRange(value: unknown): ReturnVisitRange | null {
  if (!isPlainObject(value) || !hasExactKeys(value, RANGE_KEYS)) return null;
  if (!isReturnVisitPeriod(value.period)) return null;
  if (!isCalendarDay(value.from) || !isCalendarDay(value.toExclusive)) return null;
  if (value.from > value.toExclusive) return null;
  return { period: value.period, from: value.from, toExclusive: value.toExclusive };
}

function optionalCalendarDayValid(value: unknown): boolean {
  return value === null || isCalendarDay(value);
}

function reportCoverageValid(value: Record<string, unknown>): boolean {
  return (
    optionalCalendarDayValid(value.availableFrom) &&
    optionalCalendarDayValid(value.weeklyCoverageFrom) &&
    optionalCalendarDayValid(value.monthlyCoverageFrom)
  );
}

/** Row-by-row array decode: any invalid row fails the whole array. */
function decodeRows<T>(value: unknown, decodeRow: (row: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const rows: T[] = [];
  for (const row of value) {
    const decoded = decodeRow(row);
    if (decoded === null) return null;
    rows.push(decoded);
  }
  return rows;
}

/**
 * The headline for a set must be EXACTLY weightedReturnRatePct of that same
 * set: the helper already yields null on an empty set, a zero denominator or
 * any integrity-invalid cohort, so a manufactured number over such a set (or
 * any value that is not the weighted rate) is rejected here — the plan's
 * "null for an empty set/zero denominator OR any invalid cohort in that set"
 * and "compute from the unrounded counts" rules, enforced at the boundary so
 * a future T007 bug cannot pass through to T010.
 */
function headlineMatchesSet(headline: unknown, rows: readonly ReturnCohort[]): boolean {
  if (!isRatePct(headline)) return false;
  return headline === weightedReturnRatePct(rows);
}

function reportHeadlinesValid(
  status: ReportStatus,
  weekly: readonly ReturnCohort[],
  monthly: readonly ReturnCohort[],
  weeklyRatePct: unknown,
  monthlyRatePct: unknown
): boolean {
  // empty/unconfigured responses carry null headlines (never a manufactured 0%)
  if (status !== 'ready') return weeklyRatePct === null && monthlyRatePct === null;
  return headlineMatchesSet(weeklyRatePct, weekly) && headlineMatchesSet(monthlyRatePct, monthly);
}

/** Strict decoder for the full report DTO (T007 output, validated again on T010 input). */
export function decodeReturnVisitsReport(value: unknown): ReturnVisitsReport | null {
  if (!isPlainObject(value) || !hasExactKeys(value, REPORT_KEYS)) return null;
  const scalars = decodeReportScalars(value);
  if (scalars === null) return null;
  if (value.timezone !== RETURN_VISITS_TIMEZONE) return null;
  const range = decodeReportRange(value.range);
  if (range === null) return null;
  if (!reportCoverageValid(value)) return null;
  const daily = decodeRows(value.daily, decodeReturnDay);
  const weekly = decodeRows(value.weekly, decodeReturnCohort);
  const monthly = decodeRows(value.monthly, decodeReturnCohort);
  if (daily === null || weekly === null || monthly === null) return null;
  if (!reportHeadlinesValid(scalars.status, weekly, monthly, value.weeklyRatePct, value.monthlyRatePct)) {
    return null;
  }
  return {
    version: 1,
    status: scalars.status,
    epoch: scalars.epoch,
    collectionEnabled: scalars.collectionEnabled,
    asOfUtc: scalars.asOfUtc,
    timezone: 'Europe/Rome',
    range,
    availableFrom: value.availableFrom as string | null,
    weeklyCoverageFrom: value.weeklyCoverageFrom as string | null,
    monthlyCoverageFrom: value.monthlyCoverageFrom as string | null,
    daily,
    weekly,
    monthly,
    weeklyRatePct: value.weeklyRatePct as number | null,
    monthlyRatePct: value.monthlyRatePct as number | null,
  };
}

// ---------------------------------------------------------------------------
// Period -> [S,E) Rome range (shared by T007 reader and T010 provider)
// ---------------------------------------------------------------------------

const PERIOD_DAYS: Record<Exclude<ReturnVisitPeriod, 'since-go-live'>, number> = {
  'last-7d': 7,
  'last-30d': 30,
  'last-90d': 90,
};

/**
 * Complete-day range on the Europe/Rome calendar, EXCLUDING today:
 * toExclusive=today, so the last included day is today-1. `today` is the
 * caller's explicit Rome day (romeDay(now)); `epoch` is required only for
 * since-go-live. Returns null on unknown period or invalid inputs; does NOT
 * clip to retention (coverage fields advertise truncation separately).
 */
export function resolveReturnVisitRange(
  period: string,
  today: string,
  epoch: string | null
): ReturnVisitRange | null {
  if (!isReturnVisitPeriod(period) || !isCalendarDay(today)) return null;
  if (period === 'since-go-live') {
    if (epoch === null || !isCalendarDay(epoch) || epoch > today) return null;
    return { period, from: epoch, toExclusive: today };
  }
  if (epoch !== null && (!isCalendarDay(epoch) || epoch > today)) return null;
  return { period, from: addDays(today, -PERIOD_DAYS[period]), toExclusive: today };
}

// ---------------------------------------------------------------------------
// Cohort completion rules (shared by T007 selection and T010 panels)
// ---------------------------------------------------------------------------

const COMPLETION_OFFSETS: Record<ReturnCohortMetric, number> = {
  nextWeek: RETURN_VISITS_NEXT_WEEK_COMPLETION_OFFSET_DAYS, // 14
  within30: RETURN_VISITS_WITHIN_30_COMPLETION_OFFSET_DAYS, // 37
};

/**
 * Completion-exclusive day M for a cohort week (must be a real Monday):
 * M=W+14 for nextWeek, M=W+37 for within30. The measure completes AT M;
 * completedOn (the plotted date) is addDays(M, -1). Null on invalid input.
 */
export function cohortCompletionDay(cohortWeek: string, metric: ReturnCohortMetric): string | null {
  if (!isMonday(cohortWeek)) return null;
  const offset = COMPLETION_OFFSETS[metric];
  if (offset === undefined) return null;
  return addDays(cohortWeek, offset);
}

/**
 * Eligibility of a cohort week for a report period [S,E):
 *   S < M <= E  (completion day strictly after the start, on/before the end),
 *   W >= epoch  (date compare — excludes the PARTIAL launch week when epoch
 *                falls mid-week; deliberately NOT W >= Monday(epoch)),
 *   W >= retainedCutoff (earliest bucket still stored; null = no cutoff).
 * Invalid cohort week / range inputs are not eligible (false, not a throw).
 */
export function isCohortEligible(
  cohortWeek: string,
  metric: ReturnCohortMetric,
  range: CohortRange,
  options: { epoch: string; retainedCutoff?: string | null }
): boolean {
  const completion = cohortCompletionDay(cohortWeek, metric);
  if (completion === null) return false;
  if (!isCalendarDay(range.from) || !isCalendarDay(range.toExclusive)) return false;
  if (!isCalendarDay(options.epoch)) return false;
  if (cohortWeek < options.epoch) return false;
  const cutoff = options.retainedCutoff;
  if (cutoff !== undefined && cutoff !== null) {
    if (!isCalendarDay(cutoff) || cohortWeek < cutoff) return false;
  }
  return range.from < completion && completion <= range.toExclusive;
}

/**
 * Weighted return rate of an eligible cohort set:
 * 100 * sum(returned) / sum(new). Null on an empty set, a zero denominator,
 * any integrity-invalid cohort, or malformed counts. returned>new is integrity
 * damage even when mislabeled 'ok' — null, NEVER clamped to 100.
 */
export function weightedReturnRatePct(cohorts: readonly ReturnRateInput[]): number | null {
  let sumNew = 0;
  let sumReturned = 0;
  for (const cohort of cohorts) {
    if (!isNonNegativeSafeInt(cohort.newObservations) || !isNonNegativeSafeInt(cohort.returnedObservations)) {
      return null;
    }
    if (cohort.integrity !== 'ok') return null; // 'invalid' and any unknown value alike
    if (cohort.returnedObservations > cohort.newObservations) return null;
    sumNew += cohort.newObservations;
    sumReturned += cohort.returnedObservations;
  }
  if (sumNew === 0) return null;
  return (100 * sumReturned) / sumNew;
}

// ---------------------------------------------------------------------------
// Local observation transition (pure state machine, T008 consumes this)
// ---------------------------------------------------------------------------

function enroll(epoch: string, today: string): ReturnVisitTransition {
  return {
    contribution: { version: 1, epoch, day: today, kind: 'new' },
    observation: {
      version: 1,
      epoch,
      first: today,
      last: today,
      nextWeek: false,
      within30: false,
      cohortAccepted: false,
    },
  };
}

/**
 * The pure local transition: given the stored observation (already decoded),
 * a validated config and today's explicit Rome day, produce the contribution
 * to POST (or none) and the observation to persist (unchanged when none).
 *
 * Rules:
 * - disabled config (or today before epoch) never creates state (R7);
 * - a stored record from a NEWER epoch than the received config is NOT
 *   corrupt: the stale config is ignored WITHOUT reset (no downgrade, no
 *   duplicate enrollment later);
 * - a newer, nondecreasing epoch resets into a fresh enrollment;
 * - today<=last is a no-op (same day already recorded; backwards clock);
 * - expiry at first+RETURN_VISITS_EXPIRY_DAYS: that day re-enrolls as new;
 * - each cohort flag is sent at most once (sticky booleans), and NEVER when
 *   the enrollment is unconfirmed (cohortAccepted=false) — unconfirmed
 *   browsers still contribute daily returning activity, never cohort flags;
 * - a return with no flag window left carries no cohortWeek (daily-only).
 *
 * Throws only when `today` is not a real calendar day (broken clock
 * injection — a programmer bug, not protocol data).
 */
export function transitionReturnObservation(
  stored: ReturnObservation | null,
  config: ReturnVisitsConfig,
  today: string
): ReturnVisitTransition {
  if (!isCalendarDay(today)) throw new Error(`invalid today: ${today}`);
  if (!config.enabled || config.epoch === null) return { contribution: null, observation: stored };
  if (today < config.epoch) return { contribution: null, observation: stored };

  if (stored === null) return enroll(config.epoch, today);
  if (stored.epoch > config.epoch) return { contribution: null, observation: stored }; // stale config
  if (stored.epoch < config.epoch) return enroll(config.epoch, today); // nondecreasing, advanced
  if (today <= stored.last) return { contribution: null, observation: stored };
  if (today >= addDays(stored.first, RETURN_VISITS_EXPIRY_DAYS)) return enroll(config.epoch, today);

  // It is a later day inside the observation lifetime: a return visit.
  return returnVisitOutcome(stored, today);
}

/** The two cohort-flag send decisions for a return day (each at most once, confirmed only). */
function returnFlagDecisions(stored: ReturnObservation, today: string): { sendNextWeek: boolean; sendWithin30: boolean } {
  const canFlag = stored.cohortAccepted;
  const cohortWeek = mondayOfWeek(stored.first);
  const sendNextWeek = canFlag && !stored.nextWeek && mondayOfWeek(today) === addDays(cohortWeek, 7);
  const sendWithin30 = canFlag && !stored.within30 && diffDays(today, stored.first) <= 30;
  return { sendNextWeek, sendWithin30 };
}

/** Builds the return contribution + updated record for a later day (post-guard). */
function returnVisitOutcome(stored: ReturnObservation, today: string): ReturnVisitTransition {
  const cohortWeek = mondayOfWeek(stored.first);
  const { sendNextWeek, sendWithin30 } = returnFlagDecisions(stored, today);

  let contribution: ReturnContribution;
  if (sendNextWeek || sendWithin30) {
    contribution = { version: 1, epoch: stored.epoch, day: today, kind: 'return', cohortWeek };
    if (sendNextWeek) contribution.nextWeek = true;
    if (sendWithin30) contribution.within30 = true;
  } else {
    contribution = { version: 1, epoch: stored.epoch, day: today, kind: 'return' };
  }
  return {
    contribution,
    observation: {
      version: 1,
      epoch: stored.epoch,
      first: stored.first,
      last: today,
      nextWeek: stored.nextWeek || sendNextWeek,
      within30: stored.within30 || sendWithin30,
      cohortAccepted: stored.cohortAccepted,
    },
  };
}
